import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { useAuth } from "contexts/auth-context"
import { Pagination, Transition } from "components"
import Modal from "components/modal"
import { lang } from "config"
import { Pencil } from "components/icons/outline"
import { useResultSet } from "repositories/db-repository"
// import Delete from "./delete" // kalau kamu punya delete GR, enable
import { getBranchList, buildOutletFilterOptions, getCurrentEntity, resolveOutletFilterWith } from "helpers/general-helper"
import useBranchRepositories from "repositories/branch"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import StatusInfo from "components/status-info" // 150726 (ATALLA-322)

// 150726 (ATALLA-322): penjelasan singkat tiap status penerimaan barang (GR)
// utk tombol tanda tanya di samping filter status.
const GR_STATUS_INFO = [
  { value: "DRAFT", label: "DRAFT", description: "Belum diposting; stok belum masuk." },
  { value: "POSTED", label: "POSTED", description: "Sudah diposting; stok sudah masuk." },
  { value: "CANCELLED", label: "CANCELLED", description: "Dibatalkan; stok yang sudah masuk dibalik kembali." },
]

// optional: kalau kamu mau bikin filter seperti PO, bisa reuse komponen Filter yg sama
// import Filter from "pages/purchase/filter"

export default function List() {
  const { can, currentBranch, isSuper, user } = useAuth()
  const { title, transition, setTransition } = useOutletContext()

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // 250726 (permintaan user): filter outlet penerimaan DIBEBASKAN (lintas outlet/entity),
  // TAPI default value = outlet user login. Tidak ada lagi lock ke satu outlet.
  const isBranchLocked = false

  const page = searchParams.get("page")
  const status = searchParams.get("status")
  const rawOutletParam = searchParams.get("outlet_id")
  const dateFrom = searchParams.get("date_from") ?? ""
  const dateTo = searchParams.get("date_to") ?? ""
  const searchParam = searchParams.get("search") ?? ""

  // Local state untuk search (submit on Enter)
  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)

  useEffect(() => setSearch(searchParam), [searchParam])

  const branchSelection = getBranchList()

  // 250726 (permintaan user): opsi filter outlet LINTAS-ENTITAS (daftar outlet lengkap
  // unrestricted) supaya bisa pilih outlet mana pun. Default filter = outlet user login.
  const { data: allOutletData } = useBranchRepositories({ paginate: false, unrestricted: true })
  const allOutlets = useMemo(
    () => (Array.isArray(allOutletData) ? allOutletData : allOutletData?.data ?? []),
    [allOutletData]
  )
  const outletOptions = useMemo(
    () => buildOutletFilterOptions(allOutlets, getCurrentEntity()),
    [allOutlets]
  )
  // Default = outlet user (individual), bukan grup; param absen → currentBranch.id.
  const outletFilter = rawOutletParam ?? String(currentBranch?.id ?? "")
  const { outlet_id: resolvedOutletId, branch_list: resolvedBranchList } =
    resolveOutletFilterWith(outletFilter, outletOptions)

  /**
   * ✅ ResultSet untuk GR
   * Ganti "goods_receipts" kalau nama RS kamu beda.
   *
   * Params yang umum:
   * - outlet_id
   * - branch_list
   * - page
   * - record_per_page
   * - search
   * - status
   */
  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `goods-receipts`,
    {
      outlet_id: resolvedOutletId || undefined,
      branch_list: JSON.stringify(resolvedBranchList),
      page,
      per_page: 5,
      search: searchParam || undefined,
      status: status || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }
  )

  const { data: transferReceiptResult, isLoading: isLoadingTransferReceipts, mutate: mutateTransferReceipts } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `stock-transfers`,
    {
      outlet_id: currentBranch?.id,
      // 280726 (permintaan user): transfer terima-sebagian ikut tampil — sisanya
      // bisa diterima lewat "Terima Sisa".
      status: "POSTED,PARTIAL_RECEIVED",
      with_items: 1,
      page: 1,
      per_page: 10,
    }
  )

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const patchSearchParams = (patch, opts = {}) => {
    const next = new URLSearchParams(searchParams)

    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })

    // reset page ketika filter berubah
    if (
      patch &&
      (Object.prototype.hasOwnProperty.call(patch, "search") ||
        Object.prototype.hasOwnProperty.call(patch, "status") ||
        Object.prototype.hasOwnProperty.call(patch, "outlet_id") ||
        Object.prototype.hasOwnProperty.call(patch, "date_from") ||
        Object.prototype.hasOwnProperty.call(patch, "date_to"))
    ) {
      next.delete("page")
    }

    setSearchParams(next, opts)
  }

  const removeFilter = () => setSearchParams({})

  const onSearchChange = (val) => setSearch(val)

  const onSearchSubmit = () => patchSearchParams({ search: search, page: 1 })

  const generatePaginationParam = () => {
    let tmp = ""
    if (status) tmp += `&status=${status}`
    if (outletFilter) tmp += `&outlet_id=${outletFilter}`
    if (dateFrom) tmp += `&date_from=${dateFrom}`
    if (dateTo) tmp += `&date_to=${dateTo}`
    if (searchParams.has("search")) tmp += `&search=${searchParams.get("search")}`
    return tmp
  }

  // normalize list dari response baru
  const list = useMemo(() => {
    if (Array.isArray(result_set?.data?.data)) return result_set.data?.data
    return []
  }, [result_set])

  const transferReceipts = useMemo(() => {
    if (Array.isArray(transferReceiptResult?.data?.data)) return transferReceiptResult.data.data
    return []
  }, [transferReceiptResult])

  // untuk Pagination component (kalau komponen kamu masih expect fields ini)
  const paginationLinks = result_set?.data?.links
  const total = result_set?.data?.pagination?.total
  const from = result_set?.data?.from
  const to = result_set?.data?.to

  // ATALLA-322: penjelasan tiap status — tooltip saat hover badge.
  const GR_STATUS_DESCRIPTIONS = {
    DRAFT: "GR dibuat otomatis saat PO di-approve, belum diposting — stok belum berubah.",
    POSTED: "GR sudah diposting — stok bertambah sesuai qty diterima.",
    CANCELLED: "GR dibatalkan — efek stoknya dikembalikan.",
    CANCELED: "GR dibatalkan — efek stoknya dikembalikan.",
  }

  const statusBadge = (st) => {
    const s = String(st || "").toUpperCase()
    const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium cursor-help"
    const title = GR_STATUS_DESCRIPTIONS[s] || ""
    if (s === "POSTED") return <span title={title} className={`${base} bg-green-50 text-green-700`}>{st}</span>
    if (s === "DRAFT") return <span title={title} className={`${base} bg-neutral-100 text-neutral-700`}>{st}</span>
    if (s === "CANCELLED" || s === "CANCELED") return <span title={title} className={`${base} bg-red-50 text-red-700`}>{st}</span>
    return <span title={title} className={`${base} bg-neutral-100 text-neutral-700`}>{st || "-"}</span>
  }

  // Total qty diterima per GR: pakai gr.qty_total bila backend menyediakannya,
  // else jumlahkan qty_received dari items (sudah di-eager-load di list).
  const grTotalQty = (gr) => {
    if (gr?.qty_total != null) return gr.qty_total
    if (Array.isArray(gr?.items)) {
      return gr.items.reduce((sum, it) => sum + Number(it?.qty_received ?? 0), 0)
    }
    return "-"
  }

  const formatDateTime = (value) => {
    if (!value) return "-"

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const postJson = async (path, body = null) => {
    const url = `${process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")}${path}`
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : null,
      // credentials: "include",
    })

    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      const msg = json?.message || "Permintaan gagal"
      const err = json?.error ? `: ${json.error}` : ""
      throw new Error(msg + err)
    }
    return json
  }

  const [actingId, setActingId] = useState(null)
  const [receiveDrafts, setReceiveDrafts] = useState({})
  const [detailTransfer, setDetailTransfer] = useState(null)

  const canPost = (gr) => String(gr?.status || "").toUpperCase() === "DRAFT"
  const onPost = async (gr) => {
    if (!gr?.id) return
    if (!window.confirm(`Posting Penerimaan Barang ${gr.gr_number || ""}?`)) return

    try {
      setActingId(gr.id)
      await postJson(`/goods-receipts/${gr.id}/post`, { postedBy: user?.name })
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal memposting GR")
    } finally {
      setActingId(null)
    }
  }

  const onReceiveTransfer = async (transfer) => {
    if (!transfer?.id) return
    if (!window.confirm(`Terima transfer ${transfer.delivery_order_no || ""}?`)) return

    try {
      setActingId(transfer.id)
      const received_lines = (transfer.items || [])
        .map((item) => ({
          line_id: item.id,
          qty: Number(receiveDrafts?.[transfer.id]?.[item.id] ?? item.qty_outstanding ?? 0),
        }))
        .filter((item) => item.qty > 0)

      await postJson(`/stock-transfers/${transfer.id}/receive`, { received_lines })
      await mutateTransferReceipts()
      // 280726 (laporan user): bersihkan ketikan qty transfer ini — tanpa ini,
      // membuka modal lagi menampilkan ANGKA KETIKAN LAMA (bukan default sisa
      // terbaru) sampai halaman di-refresh, karena input memprioritaskan draft
      // tersimpan di state atas qty_outstanding hasil fetch ulang.
      setReceiveDrafts((prev) => {
        const next = { ...prev }
        delete next[transfer.id]
        return next
      })
      setDetailTransfer(null)
      toast.success("Transfer berhasil diterima")
    } catch (e) {
      toast.error(e.message || "Gagal menerima transfer")
    } finally {
      setActingId(null)
    }
  }

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            {/* Search */}
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  strokeWidth={1}
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>

              <input
                ref={searchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearchSubmit() } }}
                value={search}
                type="text"
                placeholder={`${lang.search} No. GR / No. PO / No. Surat Jalan`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            {/* Filter Outlet — dibebaskan lintas outlet/entity, default outlet user. */}
            <OutletFilterSelect
              value={outletFilter}
              options={outletOptions}
              onChange={(v) => patchSearchParams({ outlet_id: v })}
              disabled={isBranchLocked}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />

            {/* Simple filter status (tanpa komponen Filter) */}
            <select
              value={status || ""}
              onChange={(e) => patchSearchParams({ status: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="">Semua Status</option>
              <option value="DRAFT">DRAFT</option>
              <option value="POSTED">POSTED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>

            {/* 150726 (ATALLA-322): tombol tanda tanya penjelasan status GR. */}
            <StatusInfo
              className="hidden sm:inline-flex self-center"
              statuses={GR_STATUS_INFO}
            />

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => patchSearchParams({ date_from: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              title="Tanggal Dari"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => patchSearchParams({ date_to: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              title="Tanggal Sampai"
            />

            {(status || searchParam || dateFrom || dateTo) && (
              <button
                type="button"
                onClick={removeFilter}
                className="hidden sm:inline-flex px-3 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
              >
                Reset
              </button>
            )}

            {/* Kalau mau pakai Filter component seperti PO:
                <Filter ... onSubmit={(v)=>patchSearchParams(v)} onRemove={removeFilter} />
            */}
          </div>

          {/* <div className="flex items-center space-x-2">
            {can("goods-receipt.create") && (
              <div>
                <Link
                  to="create"
                  onClick={() => setTransition({ type: "slide", reverse: true })}
                  state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                >
                  <Plus className="w-4 h-4" stroke={1.5} />
                  <span>{lang.create}</span>
                </Link>
              </div>
            )}
          </div> */}
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl bg-white">
          <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
            <h2 className="text-sm font-semibold text-neutral-900">Penerimaan Transfer Tertunda</h2>
            <p className="mt-1 text-xs text-neutral-500">
              Transfer stock yang sudah diposting dan menunggu diterima di outlet ini.
            </p>
          </div>
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">No DO</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Item</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Ke Outlet</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Dari Outlet</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Qty Transfer</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tanggal Transfer</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoadingTransferReceipts && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-xs text-center text-neutral-500">Memuat penerimaan transfer...</td>
                </tr>
              )}
              {!isLoadingTransferReceipts && transferReceipts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-xs text-center text-neutral-500">Tidak ada penerimaan transfer tertunda.</td>
                </tr>
              )}
              {!isLoadingTransferReceipts && transferReceipts.map((transfer) => (
                <tr key={transfer.id} className="hover:bg-brand-50 transition-colors">
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{transfer.delivery_order_no || "-"}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-700">{transfer.total_items || 0} item</span>
                      <button
                        type="button"
                        onClick={() => setDetailTransfer(transfer)}
                        className="px-2 py-1 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md"
                      >
                        Detail
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{transfer.dest_outlet_name || "-"}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{transfer.source_outlet_name || "-"}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">
                    <div>{transfer.total_qty_requested || transfer.qty || 0}</div>
                    {Number(transfer.total_qty_received ?? 0) > 0 && (
                      <div className="text-[11px] text-amber-700">diterima {transfer.total_qty_received}, sisa {(transfer.total_qty_requested || 0) - (transfer.total_qty_received || 0)}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{formatDateTime(transfer.display_date || transfer.transfer_date || transfer.created_at)}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap text-right"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Modal
          isOpen={!!detailTransfer}
          onClose={() => setDetailTransfer(null)}
          title={`Detail Transfer ${detailTransfer?.delivery_order_no || ""}`}
          size="4xl"
          content={(
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="p-3 border rounded-xl border-neutral-200">
                  <div className="text-[11px] uppercase text-neutral-500">Dari Outlet</div>
                  <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.source_outlet_name || "-"}</div>
                </div>
                <div className="p-3 border rounded-xl border-neutral-200">
                  <div className="text-[11px] uppercase text-neutral-500">No DO</div>
                  <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.delivery_order_no || "-"}</div>
                </div>
                <div className="p-3 border rounded-xl border-neutral-200">
                  <div className="text-[11px] uppercase text-neutral-500">Qty Transfer</div>
                  <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.total_qty_requested || detailTransfer?.qty || 0}</div>
                </div>
                <div className="p-3 border rounded-xl border-neutral-200">
                  <div className="text-[11px] uppercase text-neutral-500">Tanggal</div>
                  <div className="mt-1 text-sm font-medium text-neutral-900">{formatDateTime(detailTransfer?.display_date || detailTransfer?.transfer_date || detailTransfer?.created_at)}</div>
                </div>
              </div>

              <div className="overflow-hidden border rounded-xl border-neutral-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Produk</th>
                      <th className="px-3 py-2 text-right">Transfer</th>
                      {/* 280726 (permintaan user): kolom sudah diterima + sisa —
                          pola penerimaan barang dari PO. */}
                      <th className="px-3 py-2 text-right">Diterima</th>
                      <th className="px-3 py-2 text-right">Sisa</th>
                      <th className="px-3 py-2 text-right">Qty Terima</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!(detailTransfer?.items || []).length && (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-neutral-500">
                          Detail item belum tersedia.
                        </td>
                      </tr>
                    )}
                    {(detailTransfer?.items || []).map((item) => {
                      const currentValue = receiveDrafts?.[detailTransfer.id]?.[item.id] ?? item.qty_outstanding ?? 0

                      return (
                        <tr key={item.id} className="border-t hover:bg-brand-50 transition-colors">
                          <td className="px-3 py-3">{item.product_unit_name || "-"}</td>
                          <td className="px-3 py-3 text-right">{item.qty_requested ?? 0}</td>
                          <td className="px-3 py-3 text-right text-emerald-700">{item.qty_received ?? 0}</td>
                          <td className="px-3 py-3 text-right font-medium">{item.qty_outstanding ?? 0}</td>
                          <td className="px-3 py-3 text-right">
                            <input
                              type="number"
                              min={0}
                              max={item.qty_outstanding}
                              value={currentValue}
                              onChange={(event) => {
                                const nextValue = Math.max(
                                  0,
                                  Math.min(Number(event.target.value || 0), Number(item.qty_outstanding || 0))
                                )

                                setReceiveDrafts((prev) => ({
                                  ...prev,
                                  [detailTransfer.id]: {
                                    ...(prev[detailTransfer.id] || {}),
                                    [item.id]: nextValue,
                                  },
                                }))
                              }}
                              className="w-24 px-2 py-1 text-xs text-right border rounded-md"
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={actingId === detailTransfer?.id}
                  onClick={() => onReceiveTransfer(detailTransfer)}
                  className="px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl disabled:opacity-60"
                >
                  {actingId === detailTransfer?.id
                    ? "..."
                    : detailTransfer?.status === "PARTIAL_RECEIVED" ? "Terima Sisa" : "Terima"}
                </button>
              </div>
            </div>
          )}
        />

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  No GR
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  No PO
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Outlet
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Tanggal Penerimaan
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Status
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Posted
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Item
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Total Qty
                </th>
                <th className="relative px-3 py-3">
                  <span className="sr-only">{lang.action}</span>
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.loading_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && !searchParam && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && searchParam && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_result}
                  </td>
                </tr>
              )}

              {!isLoading &&
                list.map((gr) => (
                  <tr key={gr.id} className="hover:bg-brand-50 transition-colors">
                    <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                      {gr.gr_number ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      <div>{gr?.purchase_order?.po_number ?? gr?.po_number_snapshot ?? gr.po_id ?? "-"}</div>
                      {/* 240726 (permintaan user): nomor Faktur (BUKTI) dari PO, di bawah No PO.
                          Kosong bila Faktur PO belum pernah dicetak. */}
                      {gr?.po_faktur_no ? (
                        <div className="text-[11px] font-normal text-neutral-500">Faktur: {gr.po_faktur_no}</div>
                      ) : null}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {gr.outlet?.name ?? gr.branches?.name ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {formatDateTime(gr.receipt_date) ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs whitespace-nowrap">{statusBadge(gr.status)}</td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {String(gr.status || "").toUpperCase() === "POSTED" ? formatDateTime(gr.updated_at) : "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {gr.items_count ?? gr.items?.length ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {grTotalQty(gr)}
                    </td>

                    <td className="px-3 py-4 text-xs font-medium text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        {(can("goods-receipt.update") || can("goods-receipt.post")) && (
                          <Link
                            to={`${gr.id}/edit`}
                            state={{ back: title, from: location?.pathname, transition: "slide" }}
                            className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90"
                          >
                            <Pencil className="w-6 h-6" />
                          </Link>
                        )}

                        {/* 220726 (permintaan user): tombol Posting di list DISEMBUNYIKAN dulu —
                            posting hanya dari halaman detail GR (setelah qty diterima diisi). */}
                        {/* {can("goods-receipt.post") && canPost(gr) && (
                          <button
                            type="button"
                            disabled={actingId === gr.id}
                            onClick={() => onPost(gr)}
                            className="px-3 py-2 text-[11px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 disabled:opacity-60"
                          >
                            {actingId === gr.id ? "..." : "Posting"}
                          </button>
                        )} */}

                        {/* {can("goods-receipt.cancel") && canCancel(gr) && (
                          <button
                            type="button"
                            disabled={actingId === gr.id}
                            onClick={() => onCancel(gr)}
                            className="px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-60"
                          >
                            {actingId === gr.id ? "..." : "Cancel"}
                          </button>
                        )} */}

                        {/* Kalau ada delete permission & komponen delete:
                            {can("goods-receipt.delete") && <Delete data={gr} onSuccess={mutate} />}
                        */}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination params={generatePaginationParam()} links={paginationLinks} from={from} to={to} total={total} />
      </div>
    </Transition>
  )
}
